import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  TextField,
  InputAdornment,
  Badge,
  IconButton,
  Tooltip,
  Paper,
  Autocomplete,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Send as SendIcon,
  Drafts as DraftsIcon,
  Report as SpamIcon,
  Delete as TrashIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Error as ErrorIcon,
  CalendarToday as CalendarTodayIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Notifications as NotificationIcon,
  AssignmentInd as AssignmentIndIcon
} from '@mui/icons-material';
import EmailList from './EmailList';
import { Email, EmailNavigationItem } from '../../types/email';
import axios from 'axios';
import { API_BASE_URL, FILE_BASE_URL } from '../../config';
import { getFileUrl } from '../../utils/urlHelper';
import ReactDOM from 'react-dom';

interface EmailSidebarProps {
  refreshTrigger: number;
  currentFolder: string;
  onFolderChange: (folder: string) => void;
  onComposeClick: (mode: 'new' | 'reply' | 'reply-all' | 'forward', email: Email | null) => void;
  onEmailSelect: (email: Email) => void;
  selectedEmail: Email | null;
  setLoadingEmails: (loading: boolean) => void;
  loadingEmails: boolean;
  selectedPaciente: any | null;
  setSelectedPaciente: (paciente: any | null) => void;
  emails: any[];
  setEmails: React.Dispatch<React.SetStateAction<any[]>>;
  updateEmailFlag: (emailId: string, flagged: boolean) => void;
  onRestoreEmail?: (emailId: string) => void;
  onDeleteEmail?: (emailId: string) => void;
  isRestoring?: boolean;
  fetchEmails?: (paciente: { email?: string; email2?: string }, dias?: number, historialCompleto?: boolean, skip?: number, top?: number) => Promise<void>;
  loadingFolders: Record<string, boolean>;
  setLoadingFolders: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  loadingEmailContent?: string | null;
  papeleraNeedsUpdate?: boolean;
  setPapeleraNeedsUpdate?: React.Dispatch<React.SetStateAction<boolean>>;
  papeleraCount?: number;
  setPapeleraCount?: React.Dispatch<React.SetStateAction<number>>;
  newEmailsCount?: number;
  setNewEmailsCount?: React.Dispatch<React.SetStateAction<number>>;
  updateEmailsLoaded?: (loaded: boolean) => void;
  updateFolderBadges?: (callback: (foldersData: Record<string, number>) => void) => void;
  emailsLoaded?: boolean;
  clearSwitchingPatientModal?: () => void;
}

type DiasDialogPortalProps = {
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
};

function DiasDialogPortal({ open, onClose, children }: DiasDialogPortalProps) {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 99998,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{ zIndex: 99999, position: 'relative' }}>
        {children}
      </div>
    </div>,
    document.body
  );
}

const EmailSidebar: React.FC<EmailSidebarProps> = ({
  refreshTrigger,
  currentFolder,
  onFolderChange,
  onComposeClick,
  onEmailSelect,
  selectedEmail,
  setLoadingEmails,
  loadingEmails,
  selectedPaciente,
  setSelectedPaciente,
  emails,
  setEmails,
  updateEmailFlag,
  onRestoreEmail,
  onDeleteEmail,
  isRestoring = false,
  fetchEmails,
  loadingFolders,
  setLoadingFolders,
  loadingEmailContent,
  papeleraNeedsUpdate = false,
  setPapeleraNeedsUpdate,
  papeleraCount,
  setPapeleraCount,
  newEmailsCount,
  setNewEmailsCount,
  updateEmailsLoaded,
  updateFolderBadges,
  emailsLoaded,
  clearSwitchingPatientModal
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [pacientes, setPacientes] = useState<any[]>([]);
  const [loadingPacientes, setLoadingPacientes] = useState(false);
  const [diasDialogOpen, setDiasDialogOpen] = useState(false);
  const [diasAntiguedad, setDiasAntiguedad] = useState(21);
  const [historialCompleto, setHistorialCompleto] = useState(false);
  const [pacientePendiente, setPacientePendiente] = useState<any | null>(null);
  const [errorEmails, setErrorEmails] = useState<string | null>(null);
  const pacienteInputRef = useRef<HTMLInputElement>(null);
  const [emailsTotal, setEmailsTotal] = useState(0);
  const [emailsSkip, setEmailsSkip] = useState(0);
  const PAGE_SIZE = 10;
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [autocompleteInputValue, setAutocompleteInputValue] = useState('');
  
  // Estado para unread counts
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});

  // Estado para badges de carpetas (emails nuevos)
  const [folderBadges, setFolderBadges] = useState<Record<string, number>>({
    recibidos: 0,
    enviados: 0,
    borradores: 0,
    spam: 0,
    papelera: 0
  });

  // Estado para controlar carga de carpetas específicas
  const [loadedFolders, setLoadedFolders] = useState<Record<string, boolean>>({});

  // Estado para manejar cambios pendientes de días
  const [pendingDiasAntiguedad, setPendingDiasAntiguedad] = useState(21);
  const [pendingHistorialCompleto, setPendingHistorialCompleto] = useState(false);

  // ---- INICIO DE LA LÓGICA CLAVE ----

  // 1. useEffect que REACCIONA a un cambio de paciente y abre el diálogo.
  useEffect(() => {
    // Si la prop `selectedPaciente` cambia y sus emails NO están cargados,
    // significa que debemos pedir confirmación al usuario.
    if (selectedPaciente && !emailsLoaded) {
      setPacientePendiente(selectedPaciente);
      // Inicializar los valores por defecto del diálogo
      setPendingDiasAntiguedad(21);
      setPendingHistorialCompleto(false);
      setDiasDialogOpen(true);
    }
  }, [selectedPaciente, emailsLoaded]);

  // 2. useEffect que LIMPIA el diálogo si el estado global se revierte (cancelación).
  useEffect(() => {
    // Si el diálogo está abierto para un `pacientePendiente`,
    // pero la prop `selectedPaciente` ya no coincide con él
    // (porque el contexto la revirtió), cerramos y limpiamos todo.
    if (diasDialogOpen && pacientePendiente && selectedPaciente?.id !== pacientePendiente.id) {
        console.log('[DEBUG SIDEBAR] El paciente global fue revertido. Cerrando diálogo de días.');
        setDiasDialogOpen(false);
        setPacientePendiente(null);
    }
  }, [selectedPaciente, pacientePendiente, diasDialogOpen]);

  // 3. Handler para cuando el usuario CANCELA explícitamente el diálogo.
  const handleDiasDialogClose = () => {
    setDiasDialogOpen(false);
    setPacientePendiente(null); // Limpiamos el estado local.
    
    // Notificamos al contexto para que revierta el estado global.
    if (clearSwitchingPatientModal) {
        clearSwitchingPatientModal();
    }
  };

  // 4. Handler para cuando el usuario CONFIRMA la carga.
  const handleConfirmCargarEmails = async () => {
    if (!pacientePendiente) return; // Guarda de seguridad

    // El diálogo se cierra. No limpiamos pacientePendiente todavía.
    setDiasDialogOpen(false);

    // Actualizamos los parámetros de carga
    setDiasAntiguedad(pendingDiasAntiguedad);
    setHistorialCompleto(pendingHistorialCompleto);

    // Limpiamos los badges
    setFolderBadges({
      recibidos: 0, enviados: 0, borradores: 0, spam: 0, papelera: 0
    });
    
    // Hacemos la llamada a la API con `pacientePendiente`
    await fetchEmailsLocal(pacientePendiente, pendingDiasAntiguedad, pendingHistorialCompleto);
    
    // Después de que todo termina, limpiamos el estado pendiente.
    setPacientePendiente(null);
  };

  // 5. Handler para cuando el usuario selecciona un paciente del DROPDOWN.
  const handlePacienteChange = (event: React.SyntheticEvent, newValue: any | null) => {
    // Este handler es solo para el Autocomplete.
    // Llama a `setSelectedPaciente`, y el useEffect (1) se encargará del resto.
    setSelectedPaciente(newValue);
    if (newValue) {
      setFolderBadges({
        recibidos: 0, enviados: 0, borradores: 0, spam: 0, papelera: 0
      });
    }
  };

  // ---- FIN DE LA LÓGICA CLAVE ----

  // Calcular contadores por carpeta
  const folderCounts: Record<string, number> = {};
  emails.forEach(email => {
    const folder = email.folder || 'recibidos';
    folderCounts[folder] = (folderCounts[folder] || 0) + 1;
  });

  // Usar contador optimista para papelera si no está cargada
  const papeleraCountFinal = loadedFolders.papelera 
    ? folderCounts['papelera'] || 0 
    : (papeleraCount || 0);

  // Calcular total de emails para la carpeta "Todos" (excluyendo papelera)
  const totalEmails = emails.filter(email => email.folder !== 'papelera').length;

  const navigationItems: EmailNavigationItem[] = [
    { id: 'recibidos', label: 'Recibidos', icon: 'inbox', count: folderCounts['recibidos'] || 0 },
    { id: 'enviados', label: 'Enviados', icon: 'send', count: folderCounts['enviados'] || 0 },
    { id: 'borradores', label: 'Borradores', icon: 'drafts', count: folderCounts['borradores'] || 0 },
    { id: 'spam', label: 'Spam', icon: 'spam', count: folderCounts['spam'] || 0 },
    { id: 'papelera', label: 'Papelera', icon: 'trash', count: papeleraCountFinal }
  ];

  // Sincronizar el valor del autocompletado con el paciente seleccionado
  useEffect(() => {
    setAutocompleteInputValue(selectedPaciente ? selectedPaciente.nombre : '');
  }, [selectedPaciente]);

  // Cargar lista de todos los pacientes para el autocompletado
  useEffect(() => {
    const fetchPacientes = async () => {
      setLoadingPacientes(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/pacientes`);
        const pacientesOrdenados = response.data.sort((a: any, b: any) => 
          (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
        );
        setPacientes(pacientesOrdenados);
      } catch (error) {
        console.error('Error al cargar pacientes:', error);
      } finally {
        setLoadingPacientes(false);
      }
    };
    fetchPacientes();
  }, []);

  // Abrir diálogo de días cuando se selecciona un paciente
  useEffect(() => {
    // Si hay un paciente seleccionado y no hay emails cargados para él,
    // o si el paciente pendiente cambia, abrimos el diálogo.
    if (selectedPaciente && emails.length === 0) {
      setPacientePendiente(selectedPaciente);
      setDiasDialogOpen(true);
    }
  }, [selectedPaciente]);

  // NUEVO: Abrir diálogo de días cuando emailsLoaded cambia a false (desde contexto global)
  useEffect(() => {
    if (selectedPaciente && emailsLoaded === false) {
      // console.log('[DEBUG] EmailSidebar - emailsLoaded cambió a false, abriendo modal de días');
      setPacientePendiente(selectedPaciente);
      setDiasDialogOpen(true);
    }
  }, [emailsLoaded, selectedPaciente]);





  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'inbox': return <InboxIcon />;
      case 'send': return <SendIcon />;
      case 'drafts': return <DraftsIcon />;
      case 'spam': return <SpamIcon />;
      case 'trash': return <TrashIcon />;
      case 'email': return <EmailIcon />;
      default: return <InboxIcon />;
    }
  };

  const fetchEmailsLocal = async (
    paciente: { email?: string; email2?: string },
    dias: number,
    historialCompleto: boolean,
    skip = 0,
    top = PAGE_SIZE
  ) => {
    setLoadingEmails(true);
    setErrorEmails(null);
    
    // Deseleccionar email al refrescar (solo si es un refresco completo)
    if (skip === 0) {
      onEmailSelect(null as any);
    }
    
    try {
      const emailsPaciente = [paciente.email, paciente.email2].filter(Boolean);
      
      // Limpiar delta links del paciente anterior si existe
      if (selectedPaciente?.email && selectedPaciente.email !== paciente.email) {
        try {
          await axios.post(`${API_BASE_URL}/clear-delta-links`, {
            patient_email: selectedPaciente.email
          }, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          console.log(`[DEBUG] Delta links limpiados para paciente anterior: ${selectedPaciente.email}`);
        } catch (error) {
          console.warn(`[DEBUG] Error limpiando delta links: ${error}`);
        }
      }
      
      const res = await axios.post(
        `${API_BASE_URL}/emails-paciente?skip=${skip}&top=${top}`,
        {
          emails: emailsPaciente,
          dias,
          historial_completo: historialCompleto
        },
        { timeout: 120000 }
      );
      const emailsCargados = res.data.emails || [];

      setEmails(prevEmails => {
        if (skip === 0) {
          // Es un refresco completo. Hay que preservar las carpetas especiales.
          const specialFolderEmails = prevEmails.filter(e => e.folder === 'papelera' || e.folder === 'spam');
          
          const combined = [...emailsCargados, ...specialFolderEmails];
          const emailMap = new Map();
          combined.forEach(e => emailMap.set(e.id, e));
          
          return Array.from(emailMap.values());
        } else {
          // Es "Cargar más", simplemente añadimos.
          return [...prevEmails, ...emailsCargados];
        }
      });

      setEmailsTotal(res.data.total || 0);
      setEmailsSkip(skip + emailsCargados.length);
      
      // Marcar que los emails iniciales se han cargado (solo en refresco completo)
      if (skip === 0 && updateEmailsLoaded) {
        updateEmailsLoaded(true);
        console.log(`[DEBUG] Carga inicial completa para ${paciente.email}. El sondeo se activará en EmailLayout.`);
      }
      
      // NO auto-seleccionar emails al refrescar - mantener sin selección
      // La selección debe ser manual por parte del usuario
    } catch (error: any) {
      console.error('Error al cargar emails:', error);
      setEmails([]);
      
      // Verificar si es un error específico del backend sobre emails faltantes
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Debes enviar al menos un email de paciente')) {
        setErrorEmails('El paciente seleccionado no tiene emails configurados. Verifica que tenga al menos un email cargado.');
      } else {
        setErrorEmails('No se pudo cargar el historial de emails. Puede que la consulta haya demorado demasiado o haya ocurrido un error.');
      }
    } finally {
      setLoadingEmails(false);
    }
  };

  const handleFolderChange = (folder: string) => {
    // NO limpiar badge de la carpeta seleccionada al cambiar de carpeta
    // Los badges solo se limpian al hacer refresh
    
    // NO limpiar contador de emails nuevos al cambiar de carpeta
    // El contador de refresh solo se limpia al hacer refresh manual
    
    onFolderChange(folder);
    
    // Si es papelera y no está cargada, cargarla
    if (folder === 'papelera' && !loadedFolders.papelera && selectedPaciente) {
      setLoadingFolders(prev => ({ ...prev, papelera: true }));
      fetchSpecificFolder('papelera');
    }
  };

  // Al cambiar de paciente, limpiar emails y detalle
  useEffect(() => {
    setEmails([]);
    setEmailsTotal(0);
    setEmailsSkip(0);
    setLoadedFolders({ papelera: false });
    onEmailSelect(null as any);
    // Resetear contador de papelera
    if (setPapeleraCount) {
      setPapeleraCount(0);
    }
  }, [selectedPaciente]);

  // Resetear estado de papelera cuando cambien los días o historial completo (solo cuando se acepta)
  useEffect(() => {
    setLoadedFolders(prev => ({ ...prev, papelera: false }));
  }, [diasAntiguedad, historialCompleto]);

  // Botón para cargar más emails
  const handleLoadMore = () => {
    if (selectedPaciente) {
      fetchEmailsLocal(selectedPaciente, diasAntiguedad, historialCompleto, emailsSkip, PAGE_SIZE);
    }
  };

  // Función simplificada: ya no maneja la lógica de restauración
  const handleRestoreEmail = async (emailId: string) => {
    // Esta función ya no hace nada aquí, la lógica se maneja en EmailLayout
    console.log('[DEBUG] Restauración manejada por EmailLayout');
  };

  const fetchSpecificFolder = async (carpeta: 'papelera') => {
    if (!selectedPaciente || loadingFolders[carpeta] || loadedFolders[carpeta]) return;

    setLoadingFolders(prev => ({ ...prev, [carpeta]: true }));
    try {
      const emailsPaciente = [selectedPaciente.email, selectedPaciente.email2].filter(Boolean);
      const res = await axios.post(
        `${API_BASE_URL}/emails-carpeta-especifica?carpeta=${carpeta}`,
        { 
          emails: emailsPaciente, 
          dias: diasAntiguedad, 
          historial_completo: historialCompleto
        },
        { timeout: 120000 }
      );

      const emailsCargados = res.data.emails || [];
      setEmails(prev => {
        // Filtrar emails existentes de la misma carpeta
        const filtered = prev.filter(e => e.folder !== carpeta);
        return [...filtered, ...emailsCargados];
      });
      setLoadedFolders(prev => ({ ...prev, [carpeta]: true }));
      
      // Resetear el indicador de actualización si es la papelera
      if (carpeta === 'papelera' && setPapeleraNeedsUpdate) {
        setPapeleraNeedsUpdate(false);
        // Sincronizar el contador optimista con el contador real
        if (setPapeleraCount) {
          setPapeleraCount(emailsCargados.length);
        }
      }
    } catch (error) {
      console.error(`Error al cargar ${carpeta}:`, error);
    } finally {
      setLoadingFolders(prev => ({ ...prev, [carpeta]: false }));
    }
  };

  // Cargar contadores de no leídos
  const fetchFolderCounts = async () => {
    if (!selectedPaciente) return;
    try {
      // NO recargar todos los emails, solo actualizar contadores si es necesario
      // Los contadores ya se calculan automáticamente desde el estado local
      console.log("Contadores actualizados desde el estado local");
      
      // Solo si necesitamos contadores específicos de la API (como no leídos)
      // podríamos hacer una llamada específica aquí, pero por ahora no es necesario
      
    } catch (error) {
      console.error("Error al actualizar contadores:", error);
    }
  };

  // 3. useEffect para reaccionar al gatillo de refresco
  useEffect(() => {
    if (refreshTrigger > 0 && selectedPaciente) { // Se activa solo después del primer cambio
      console.log("Detectado trigger de refresco. Recargando emails de carpetas principales.");
      // Recargar emails de las carpetas principales (no papelera) cuando se restaura un email
      fetchEmailsLocal(selectedPaciente, diasAntiguedad, historialCompleto, 0, PAGE_SIZE);
    }
  }, [refreshTrigger]);

  // Función para actualizar badges de carpetas
  const updateFolderBadgesLocal = (foldersData: Record<string, number>) => {
    console.log('[DEBUG] Datos de badges recibidos del backend:', foldersData);
    
    // Mapear nombres de carpetas del backend (inglés) a frontend (español)
    const mappedFoldersData: Record<string, number> = {
      recibidos: foldersData.inbox || 0,
      enviados: foldersData.sentItems || 0,
      borradores: foldersData.drafts || 0,
      spam: foldersData.junkEmail || 0,
      papelera: foldersData.deleteditems || 0
    };
    
    console.log('[DEBUG] Datos mapeados a español:', mappedFoldersData);
    
    setFolderBadges(prev => {
      const newBadges = {
        ...prev,
        ...mappedFoldersData
      };
      console.log('[DEBUG] Estado final de badges:', newBadges);
      return newBadges;
    });
  };

  // Función para limpiar badge de una carpeta específica
  const clearFolderBadge = (folderId: string) => {
    // Mapear nombres de carpetas del frontend (español) a backend (inglés) si es necesario
    const folderMapping: Record<string, string> = {
      recibidos: 'inbox',
      enviados: 'sentItems',
      borradores: 'drafts',
      spam: 'junkEmail',
      papelera: 'deleteditems'
    };
    
    // Usar el nombre en español directamente ya que folderBadges usa nombres en español
    setFolderBadges(prev => ({
      ...prev,
      [folderId]: 0
    }));
    
    console.log(`[DEBUG] Badge limpiado para carpeta: ${folderId}`);
  };

  // Sincronizar badges cuando lleguen del componente padre
  useEffect(() => {
    if (updateFolderBadges) {
      // Exponer la función local al componente padre
      updateFolderBadges(updateFolderBadgesLocal);
    }
  }, [updateFolderBadges]);

  // DEBUG - Verificar cuando cambian los badges
  useEffect(() => {
    // console.log('[DEBUG] Estado de badges actualizado:', folderBadges);
  }, [folderBadges]);

  return (
    <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', backgroundColor: 'white' }}>
      {/* Header con botón de nuevo email */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Emails
          </Typography>
          <Tooltip title="Nuevo email">
            <span>
              <IconButton 
                onClick={() => onComposeClick('new', null)} 
                color="primary"
                disabled={!selectedPaciente || loadingEmails || loadingFolders.papelera || isRestoring}
              >
              <AddIcon />
            </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={selectedPaciente ? 
            `Actualizar emails de ${selectedPaciente.nombre} (${newEmailsCount || 0} nuevo(s) detectado(s))` : 
            "Selecciona un paciente para actualizar"
          }>
            <span>
              <IconButton 
                disabled={!selectedPaciente || loadingEmails || isRestoring}
                onClick={() => {
                  if (selectedPaciente) {
                    setPacientePendiente(selectedPaciente);
                    // Inicializar estados pendientes con valores actuales
                    setPendingDiasAntiguedad(diasAntiguedad);
                    setPendingHistorialCompleto(historialCompleto);
                    setDiasDialogOpen(true);
                    // Limpiar contador de emails nuevos al refrescar
                    if (setNewEmailsCount) {
                      setNewEmailsCount(0);
                    }
                  }
                }}
                sx={{
                  opacity: (!selectedPaciente || loadingEmails || isRestoring) ? 0.5 : 1,
                  color: (!selectedPaciente) ? undefined : 'primary.dark',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <Badge 
                  badgeContent={newEmailsCount || 0} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#d32f2f',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      minWidth: '20px',
                      height: '20px',
                      borderRadius: '10px'
                    }
                  }}
                >
                  <RefreshIcon />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Configuración">
            <IconButton disabled>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Selector de paciente - Profesional */}
        <Box sx={{ mb: 2 }}>
          {selectedPaciente ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Avatar
                src={getFileUrl(selectedPaciente?.ruta_imagen_perfil)}
                sx={{ width: 28, height: 28, mr: 1 }}
              >
                {selectedPaciente.nombre ? (
                  <PersonIcon />
                ) : null}
              </Avatar>
              <Typography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
                {selectedPaciente.nombre || 'Selecciona un paciente'}
              </Typography>
              <Tooltip title="Ver detalles del paciente">
                <IconButton 
                  size="small" 
                  color="primary" 
                  onClick={() => navigate(`/pacientes/${selectedPaciente.id}`)}
                  disabled={loadingEmails || loadingFolders.papelera || isRestoring}
                >
                  <AssignmentIndIcon />
                </IconButton>
              </Tooltip>
              <IconButton size="small" color="error" onClick={() => {
                setSelectedPaciente(null);
                setEmails([]);
                setErrorEmails(null);
                setAutocompleteInputValue('');
                setAutocompleteOpen(false);
                
                // Limpiar badges al cerrar selección de paciente
                setFolderBadges({
                  recibidos: 0,
                  enviados: 0,
                  borradores: 0,
                  spam: 0,
                  papelera: 0
                });
                
                setTimeout(() => {
                  pacienteInputRef.current?.focus();
                }, 100);
              }}
              disabled={loadingEmails || loadingFolders.papelera || isRestoring}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
          <Autocomplete
            options={pacientes}
            loading={loadingPacientes}
            open={autocompleteOpen}
            onOpen={() => {
              // Abrir siempre que se haga clic en el input
              setAutocompleteOpen(true);
            }}
            onClose={() => setAutocompleteOpen(false)}
            inputValue={autocompleteInputValue}
            onInputChange={(event, newInputValue) => {
              setAutocompleteInputValue(newInputValue);
              // Mantener abierto el dropdown siempre
              setAutocompleteOpen(true);
            }}
            getOptionLabel={(option) => option.nombre || ''}
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              // Si no hay término de búsqueda, mostrar todos los pacientes ordenados alfabéticamente
              if (!searchTerm.trim()) {
                return options.sort((a, b) => 
                  (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
                );
              }
              // Si hay término de búsqueda, filtrar y ordenar
              return options.filter(option => {
                const nombre = (option.nombre || '').toLowerCase();
                const email = (option.email || '').toLowerCase();
                const email2 = (option.email2 || '').toLowerCase();
                return nombre.includes(searchTerm) || 
                       email.includes(searchTerm) ||
                       email2.includes(searchTerm);
              }).sort((a, b) => 
                (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
              );
            }}
            value={selectedPaciente}
            onChange={handlePacienteChange}
            isOptionEqualToValue={(option, value) => option.medico_id === value.medico_id && option.id === value.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar
                  src={getFileUrl(option?.ruta_imagen_perfil)}
                  sx={{ width: 28, height: 28, mr: 1 }}
                >
                  {option.nombre ? (
                    <PersonIcon />
                  ) : null}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.nombre || 'Sin nombre'}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {option.email}
                  </Typography>
                  {option.email2 && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'grey.600',
                        fontSize: '0.7rem',
                        fontStyle: 'italic',
                        display: 'block'
                      }}
                    >
                      {option.email2}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Seleccionar paciente"
                placeholder="Buscar paciente..."
                autoComplete="off"
                  inputRef={pacienteInputRef}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: '#f4f6fa',
                  borderRadius: 2,
                  fontWeight: 500,
                  fontSize: 15,
                }}
              />
            )}
          />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Selecciona un paciente para ver sus emails
          </Typography>
        </Box>

        {/* Búsqueda */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar en emails..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              // Cambiar el color del borde cuando hay texto
              '& .MuiOutlinedInput-root': {
                borderColor: searchQuery.trim() ? 'primary.main' : undefined,
                '&:hover': {
                  borderColor: searchQuery.trim() ? 'primary.dark' : undefined,
                },
                '&.Mui-focused': {
                  borderColor: searchQuery.trim() ? 'primary.main' : undefined,
                }
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Busca en el contenido y asunto de los emails
          </Typography>
          {/* Indicador de filtro activo */}
          {searchQuery.trim() && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`Filtro: "${searchQuery}"`}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={() => setSearchQuery('')}
                deleteIcon={<CloseIcon />}
                sx={{ 
                  fontSize: '0.75rem',
                  height: 24,
                  '& .MuiChip-label': {
                    px: 1
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {emails.filter(email => {
                  if (!email || !email.id) return false;
                  const matchesSearch = 
                    email.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    email.from?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    email.from?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    email.bodyPreview?.toLowerCase().includes(searchQuery.toLowerCase());
                  return matchesSearch;
                }).length} resultados
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Banner de referencia de días de antigüedad */}
      {selectedPaciente && (
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Paper elevation={0} sx={{ bgcolor: '#e3f2fd', px: 2, py: 0.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
              {historialCompleto ? 'Mostrando todo el historial' : `Mostrando emails de los últimos ${diasAntiguedad} días`}
            </Typography>
          </Paper>
          <Button size="small" variant="text" onClick={() => setDiasDialogOpen(true)} sx={{ ml: 1, minWidth: 0, px: 1 }}
          disabled={loadingEmails || loadingFolders.papelera || isRestoring}
          >
            Cambiar
          </Button>
        </Box>
      )}

      {/* Navegación de carpetas */}
      <Box sx={{ flexShrink: 0 }}>
        <List dense>
          {/* Carpeta "Historial Completo" con estilo especial */}
          <ListItem disablePadding>
            <ListItemButton
              selected={currentFolder === 'todos'}
              onClick={() => handleFolderChange('todos')}
              disabled={loadingEmails || emails.length === 0 || loadingFolders.papelera}
              sx={{
                backgroundColor: currentFolder === 'todos' ? 'primary.main' : 'rgba(25, 118, 210, 0.04)',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: '#ffffff !important',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemText-primary': {
                    color: '#ffffff !important',
                    fontWeight: 600,
                  },
                  '& .MuiListItemIcon-root': {
                    color: '#ffffff !important',
                  },
                  '& .email-count-selected': {
                    color: '#ffffff !important',
                    fontWeight: 600,
                  }
                },
                '&:hover': {
                  backgroundColor: currentFolder === 'todos' ? 'primary.dark' : 'rgba(25, 118, 210, 0.08)',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <EmailIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Historial Completo"
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: currentFolder === 'todos' ? 600 : 500
                }}
              />
              {totalEmails > 0 && (
                <span
                  className={currentFolder === 'todos' ? 'email-count-selected' : undefined}
                  style={{ 
                    fontWeight: 600, 
                    transition: 'color 0.2s', 
                    marginLeft: 4,
                    color: currentFolder === 'todos' ? '#ffffff' : 'inherit'
                  }}
                >
                  {totalEmails}
                </span>
              )}
            </ListItemButton>
          </ListItem>

          {/* Línea divisoria */}
          <Divider sx={{ my: 1 }} />

          {/* Resto de carpetas */}
          {navigationItems.map((folder) => (
            <ListItem key={folder.id} disablePadding>
              <Tooltip 
                title={folderBadges[folder.id] > 0 ? 
                  `Hay ${folderBadges[folder.id]} email(s) nuevo(s) en ${folder.label}. Haz clic en el botón de actualizar para verlos.` : 
                  `Ver emails de ${folder.label}`
                }
                placement="right"
                arrow
              >
                <ListItemButton
                  selected={currentFolder === folder.id}
                  onClick={() => handleFolderChange(folder.id)}
                  disabled={loadingEmails || emails.length === 0 || loadingFolders.papelera}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      color: '#f5f6fa !important',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                      },
                      '& .MuiListItemText-primary': {
                        color: '#f5f6fa !important',
                        fontWeight: 600,
                      },
                      '& .MuiListItemIcon-root': {
                        color: '#f5f6fa !important',
                      },
                      '& .email-count-selected': {
                        color: '#f5f6fa !important',
                        fontWeight: 600,
                      }
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Badge 
                      badgeContent={folderBadges[folder.id] || 0} 
                      color="error"
                      invisible={!folderBadges[folder.id] || folderBadges[folder.id] === 0}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: '#d32f2f',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          minWidth: '18px',
                          height: '18px',
                          borderRadius: '9px'
                        }
                      }}
                    >
                      {getIcon(folder.icon)}
                    </Badge>
                  </ListItemIcon>
                  <ListItemText 
                    primary={folder.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: currentFolder === folder.id ? 600 : 400
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Indicador de carga - ahora a la izquierda */}
                    {(folder.id === 'papelera') && loadingFolders[folder.id as 'papelera'] && (
                      <CircularProgress 
                        size={16} 
                        sx={{ 
                          color: currentFolder === folder.id ? '#ffffff' : 'primary.main'
                        }}
                      />
                    )}
                  
                    {/* Indicador de restauración - ahora a la izquierda */}
                    {(folder.id === 'papelera') && isRestoring && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CircularProgress 
                          size={16} 
                          sx={{ 
                            color: currentFolder === folder.id ? '#ffffff' : 'primary.main'
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.65rem',
                            color: currentFolder === folder.id ? '#ffffff' : 'primary.main',
                            fontWeight: 500
                          }}
                        >
                          Restaurando...
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Botón de carga para papelera */}
                    {(folder.id === 'papelera') && !loadedFolders[folder.id as 'papelera'] && !loadingFolders[folder.id as 'papelera'] && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Llamar a la función principal de cambio de carpeta
                          handleFolderChange(folder.id as 'papelera');
                        }}
                        disabled={!selectedPaciente}
                        sx={{ 
                          minWidth: 'auto', 
                          px: 1, 
                          py: 0.5, 
                          fontSize: '0.75rem',
                          height: 24,
                          color: currentFolder === folder.id ? '#ffffff' : 'primary.main',
                          borderColor: currentFolder === folder.id ? '#ffffff' : 'primary.main',
                          '&:hover': {
                            borderColor: currentFolder === folder.id ? '#ffffff' : 'primary.dark',
                            backgroundColor: currentFolder === folder.id ? 'rgba(255,255,255,0.1)' : 'rgba(25,118,210,0.04)'
                          }
                        }}
                      >
                        Cargar
                      </Button>
                    )}
                    
                    {/* Contador de emails - ahora en cursiva para papelera */}
                    {(folder.count || 0) > 0 && (
                      <span
                        className={currentFolder === folder.id ? 'email-count-selected' : undefined}
                        style={{ 
                          fontWeight: 600, 
                          transition: 'color 0.2s', 
                          marginLeft: 4,
                          fontStyle: folder.id === 'papelera' ? 'italic' : 'normal' // Cursiva solo para papelera
                        }}
                      >
                        {folder.count || 0}
                      </span>
                    )}
                  </Box>
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider />

      {/* Lista de emails y botón cargar más */}
      <Box sx={{ 
        maxHeight: '40vh', 
        overflow: 'auto', 
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        {errorEmails ? (
          <Box sx={{ p: 2, color: 'error.main', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <ErrorIcon sx={{ fontSize: 24 }} />
            <Typography variant="body2">
              {errorEmails}
            </Typography>
          </Box>
        ) : (
          <>
          <EmailList
            folder={currentFolder}
            searchQuery={searchQuery}
            onEmailSelect={onEmailSelect}
            selectedEmail={selectedEmail}
            paciente={selectedPaciente}
            emails={emails}
            loading={loadingEmails}
            hasEmailsLoaded={emails.length > 0}
            folderSelected={!!currentFolder}
            updateEmailFlag={updateEmailFlag}
            onDeleteEmail={onDeleteEmail}
            onRestoreEmail={onRestoreEmail || handleRestoreEmail}
            loadingFolders={loadingFolders}
            loadingEmailContent={loadingEmailContent}
          />
            {emails.length < emailsTotal && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Button 
                  variant="outlined" 
                  onClick={handleLoadMore} 
                  disabled={loadingEmails || loadingFolders.papelera || isRestoring || !selectedPaciente}
                  startIcon={loadingEmails ? <CircularProgress size={16} /> : undefined}
                >
                  {loadingEmails ? 'Cargando...' : 'Cargar más'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Diálogo para elegir días de antigüedad */}
      <DiasDialogPortal open={diasDialogOpen} onClose={handleDiasDialogClose}>
        <Box sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          minWidth: 320,
          maxWidth: '90vw',
        }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            ¿Cuántos días de emails quieres cargar?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Días de antigüedad"
              type="number"
              value={pendingDiasAntiguedad}
              onChange={e => setPendingDiasAntiguedad(Number(e.target.value))}
              disabled={pendingHistorialCompleto}
              inputProps={{ min: 1, max: 365 }}
            />
            <FormControlLabel
              control={<Checkbox checked={pendingHistorialCompleto} onChange={e => setPendingHistorialCompleto(e.target.checked)} />}
              label="Cargar historial completo"
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button onClick={handleDiasDialogClose}>Cancelar</Button>
            <Button variant="contained" onClick={handleConfirmCargarEmails}>
              Cargar emails
            </Button>
          </Box>
        </Box>
      </DiasDialogPortal>
    </Box>
  );
};

export default EmailSidebar; 